import { enums, resources, common } from '../gads'
import { GadsFieldsRequired } from '../utils/global'
import { UUID } from '..'
import * as ExperimentSettings from './experiment_settings'

export const cpaBiddingStrategies = [
    enums.BiddingStrategyType.TARGET_CPA,
    enums.BiddingStrategyType.MAXIMIZE_CONVERSIONS,
    enums.BiddingStrategyType.TARGET_SPEND,
    enums.BiddingStrategyType.ENHANCED_CPC,
    enums.BiddingStrategyType.MANUAL_CPC,
    // enums.BiddingStrategyType.TARGET_IMPRESSION_SHARE,
]

export const roasBiddingStrategies = [
    enums.BiddingStrategyType.TARGET_ROAS,
    enums.BiddingStrategyType.MAXIMIZE_CONVERSION_VALUE,
]

export const smartBiddingStrategies = [
    enums.BiddingStrategyType.TARGET_CPA,
    enums.BiddingStrategyType.TARGET_ROAS,
    enums.BiddingStrategyType.MAXIMIZE_CONVERSIONS,
    enums.BiddingStrategyType.MAXIMIZE_CONVERSION_VALUE,
    enums.BiddingStrategyType.ENHANCED_CPC,
    enums.BiddingStrategyType.TARGET_SPEND,
]

export interface Campaign {
    campaignId: number
    campaignName: string
    servingStatus: enums.CampaignServingStatus
    campaignStatus: enums.CampaignStatus
    experimentType: enums.CampaignExperimentType
    isSharedBudget: boolean
    isUsingCampaignLevelConversionGoals: boolean
    biddingStrategySystemStatus: enums.BiddingStrategySystemStatus
    advertisingChannelType: enums.AdvertisingChannelType
    hasRunningExperiment: boolean
    hasDynamicConversionValues: boolean
    metrics: {
        thirtyDayClicks: number
        thirtyDayConversions: number
        allTimeConversions: number
        thirtyDayConversionsValue: number
        thirtyDayCost: number
        thirtyDayImpressions: number
        thirtyDayCPA: number | 'Infinity'
        thirtyDayROAS: number | 'Infinity'
        impressionSharePercent: number // keep as decimal 0.5 => 50%
        ninetyDayCost: number
        ninetyDayCPC: number
        ninetyDayCPA: number
        ninetyDayROAS: number
        zeroConversionWeekCostToTotalCostRatio: number
    }
    budgetCapStatusPercent: number // 0 if no budget
    biddingStrategyType: enums.BiddingStrategyType
    campaignGroupId: UUID | null
    isBrandingCampaign: boolean
    hasHadRecentExperiment: boolean
    firstCheckThatFailsText?: string
}

export interface CampaignWithRecommendation extends Campaign {
    recommendedBidStrategy?: enums.BiddingStrategyType
}

export interface ExperimentRecommendation {
    biddingStrategyType: enums.BiddingStrategyType
    recommendedBidStrategy: enums.BiddingStrategyType
    campaigns: { campaignId: number; campaignName: string }[]
}

export interface GetCampaignsAndExperimentsResponse {
    eligibleCampaigns: Campaign[]
    ineligibleCampaigns: Campaign[]
    experimentRecommendations: ExperimentRecommendation[]
}

export interface CreateExperimentRequest<T = ExperimentSettings.ExperimentSettings> {
    experimentName: string
    experimentDescription: string
    currentBiddingStrategy: enums.BiddingStrategyType
    experimentBaseBudgetAllocation: number
    experimentDurationDays: number
    campaignIds: number[]
    advertisingChannelType:
        | enums.AdvertisingChannelType.SEARCH
        | enums.AdvertisingChannelType.DISPLAY
    settings: T
    isRecommendation: boolean
}

export type CreateExperimentResponse =
    | { succeeded: true; experimentId: string }
    | { succeeded: false; error: string }
export type PromoteExperimentResponse = { succeeded: boolean; failedCompleted?: FailedCompleted[] }
export type EndExperimentResponse = { succeeded: boolean; failedCompleted?: FailedCompleted[] }

export type PopulateExperimentCampaignsResponse =
    | { succeeded: true; status: OpteoExperimentStatus }
    | { succeeded: false; error?: string }

export interface CampaignExperimentMetrics {
    campaignId: GadsFieldsRequired<resources.Campaign, 'id'>
    campaignName: GadsFieldsRequired<resources.Campaign, 'name'>
    campaignResourceName: GadsFieldsRequired<resources.Campaign, 'resource_name'>
    clicks: GadsFieldsRequired<common.Metrics, 'clicks'>
    cost: GadsFieldsRequired<common.Metrics, 'cost_micros'>
    conversions: GadsFieldsRequired<common.Metrics, 'all_conversions'>
    conversionValue: GadsFieldsRequired<common.Metrics, 'all_conversions_value'>
    impressions: GadsFieldsRequired<common.Metrics, 'impressions'>
    impressionSharePercent: GadsFieldsRequired<common.Metrics, 'search_impression_share'>
}

export interface CampaignExperimentResourceNames {
    baseCampaignResourceName: string
    baseCampaignName: string
    experimentCampaignResourceName: string
    experimentCampaignName: string
    experimentResourceName: string
}

export interface CampaignExperiment {
    optExperimentId: UUID
    description: string
    name: string
    metrics?: CampaignExperimentMetrics[]
    currentBiddingStrategy: enums.BiddingStrategyType
    campaignIds: CampaignExperimentResourceNames[]
    startDate: string
    creationDate: string
    endDate: string
    baseBudgetAllocation: number
    status: OpteoExperimentStatus
    settings: ExperimentSettings.ExperimentSettings
    failedCompleted: FailedCompleted[] | null
}

export interface PerformanceData {
    name?: string
    id?: string
    resourceName?: string
    clicks: number
    cost: number
    allConversions: number
    allConversionsValue: number
    searchImpressionShare: number
    searchAbsoluteTopImpressionShare: number
    impressions: number
    date?: string
}

export interface ExperimentDb {
    experiment_id: UUID
    experiment_settings: ExperimentSettings.ExperimentSettings
    experiment_campaign_ids: CampaignExperimentResourceNames[]
    experiment_name: string
    experiment_description: string
    current_bidding_strategy: keyof typeof enums.BiddingStrategyType
    experiment_bidding_strategy: keyof typeof enums.BiddingStrategyType
    experiment_base_budget_allocation: number
    experiment_creation_date: string
    experiment_start_date: string
    experiment_end_date: string
    status: keyof typeof OpteoExperimentStatus
    failed_completed: FailedCompleted[] | null
}

export interface CampaignExperimentPair {
    googleExperimentId: number
    googleExperimentResourceName: string
    isExperimentWinning: boolean | null
    baseCampaignMetrics: PerformanceData
    experimentCampaignMetrics: PerformanceData
}

/**
 * @name CompleteExperimentRequest
 * @description This is the request type format for completing and experiment
 */
export interface CompleteExperimentRequest {
    experiment: GetAccountExperimentDataResponse
}

/**
 * @name GetAccountExperimentDataResponse
 * @description This is the type for all active and completed experiment data being sent to the frontend
 */
export interface GetAccountExperimentDataResponse {
    optExperimentId: UUID
    name: string
    description: string
    experimentTotalData: PerformanceData & {
        performanceData: PerformanceData[]
    }
    baseTotalData: PerformanceData & {
        performanceData: PerformanceData[]
    }
    campaignExperimentPairs: CampaignExperimentPair[]
    creationDate: string
    startDate: string
    endDate: string
    actualEndDate: string
    isExperimentWinning: boolean | null
    confidenceLevel: number | null
    confidenceBasedOn: string | null
    currentBiddingStrategy: enums.BiddingStrategyType
    status: OpteoExperimentStatus
    settings: ExperimentSettings.ExperimentSettings
    baseBudgetAllocation: number
    failedCompleted: FailedCompleted[] | null
}

export enum OpteoExperimentStatus {
    CREATING = 'CREATING',
    LEARNING = 'LEARNING',
    ACTIVE = 'ACTIVE',
    COMPLETED = 'COMPLETED',
    COMPLETING = 'COMPLETING',
    CANCELLED = 'CANCELLED',
    REMOVING = 'REMOVING',
    FAILED_COMPLETE = 'FAILED_COMPLETE',
}

export interface FailedCompleted {
    experimentResourceName: string
    intent: Intent
    error: string
    displayError: string
}

export const KnownFailedCompleteErrors = {
    ERROR_ENDING_EXPERIMENT: 'There was an unexpected error ending this experiment.',
    ERROR_PROMOTING_EXPERIMENT: 'There was an unexpected error promoting this experiment.',
    [`The error code is not in this version.`]:
        'There was an unexpected error with the Google Ads API.',
    [`The end date of an expired experiment cannot be modified.`]:
        'The end date of an expired experiment cannot be modified.',
    [`Experiments can only be modified when they are ENABLED.`]:
        'Experiments can only be modified when they are enabled.',
    DEFAULT: 'There was an unexpected error pushing this experiment.',
}

export enum Intent {
    END = 'END',
    PROMOTE = 'PROMOTE',
}

export * as ExperimentSettings from './experiment_settings'
